import type { FC, MouseEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { format } from 'date-fns';
import { parseUtc2AsLocal } from '../utils/startTime';

interface StartTimeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSetTime: (isoDateTime: string) => void;
  onRemove: () => void;
  currentValue: string | undefined;
}

const PAPER_SX = { maxWidth: 444, width: '90%' } as const;

const stopPropagation = (e: MouseEvent) => {
  e.stopPropagation();
};

const getTodayDate = (): string => format(new Date(), 'yyyy-MM-dd');

const parseInitialValue = (value: string | undefined) => ({
  date: value?.split('T')[0] ?? getTodayDate(),
  time: value?.split('T')[1]?.slice(0, 5) ?? '',
});

const getLocalTimeHint = (date: string, time: string): string => {
  if (!date || !time) return '';
  try {
    const localDate = parseUtc2AsLocal(`${date}T${time}:00`);
    if (isNaN(localDate.getTime())) return '';
    return format(localDate, 'dd.MM.yy HH:mm');
  } catch {
    return '';
  }
};

const StartTimeDialogContent: FC<Omit<StartTimeDialogProps, 'isOpen'>> = ({
  onClose,
  onSetTime,
  onRemove,
  currentValue,
}) => {
  const initial = parseInitialValue(currentValue);
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);

  const isSetTimeDisabled = !date || !time;

  const localTimeHint = useMemo(
    () => getLocalTimeHint(date, time),
    [date, time],
  );

  const handleSetTime = useCallback(() => {
    onSetTime(`${date}T${time}:00`);
    onClose();
  }, [date, time, onSetTime, onClose]);

  const handleRemove = useCallback(() => {
    onRemove();
    onClose();
  }, [onRemove, onClose]);

  return (
    <>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        Start time for adset
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => { setDate(e.target.value); }}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Time"
            type="time"
            value={time}
            onChange={(e) => { setTime(e.target.value); }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 60 }}
            fullWidth
          />
        </Box>
        {localTimeHint && (
          <Typography variant="caption" sx={{ color: 'text.disabled', mt: 1, display: 'block' }}>
            Local time: {localTimeHint}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2.5 }}>
        <Button
          onClick={handleRemove}
          color="error"
          disabled={!currentValue}
        >
          Remove start time
        </Button>
        <Button
          onClick={handleSetTime}
          variant="contained"
          disabled={isSetTimeDisabled}
        >
          Set time
        </Button>
      </DialogActions>
    </>
  );
};

export const StartTimeDialog: FC<StartTimeDialogProps> = ({
  isOpen,
  onClose,
  ...contentProps
}) => (
  <Dialog
    open={isOpen}
    onClose={onClose}
    PaperProps={{ sx: PAPER_SX }}
    onClick={stopPropagation}
  >
    {isOpen && <StartTimeDialogContent onClose={onClose} {...contentProps} />}
  </Dialog>
);
